import type { ThemeVariables } from '@/types'
import { BorderStyle, ButtonStyle, CornerRoundness, ActionSlot } from '@/constants'

const CARD_ROUNDNESS: Record<string, string> = {
  [CornerRoundness.Square]: '0px',
  [CornerRoundness.GentlyRounded]: '12px',
  [CornerRoundness.FullyRounded]: '9999px',
}

const BUTTON_ROUNDNESS: Record<string, string> = {
  [CornerRoundness.Square]: '0px',
  [CornerRoundness.GentlyRounded]: '10px',
  [CornerRoundness.FullyRounded]: '9999px',
}

const ASPECT_RATIO: Record<string, string> = {
  square: '1 / 1',
  portrait: '3 / 4',
  landscape: '4 / 3',
}

const LOGO_JUSTIFY: Record<string, string> = {
  'top-left': 'flex-start',
  'top-center': 'center',
  'top-right': 'flex-end',
}

const RESOLVED_THEME = new WeakMap<object, ThemeVariables>()

// style_omp_theme -> style_variables -> factory default. Cached because the result feeds a useEffect dep.
export function getThemeVariables(campaign: any): ThemeVariables | undefined {
  if (!campaign) {
    return undefined
  }
  const cached = RESOLVED_THEME.get(campaign)
  if (cached) {
    return cached
  }
  const style = campaign.styles?.[0]
  const brand = style?.variables ?? campaign.style_variables
  const portal = style?.omp_theme ?? campaign.style_omp_theme
  if (!brand && !portal) {
    return undefined
  }
  const merged: Record<string, any> = { ...(brand ?? {}) }
  Object.entries(portal ?? {}).forEach(([key, value]) => {
    if (value != null && value !== '') {
      merged[key] = value
    }
  })
  RESOLVED_THEME.set(campaign, merged as ThemeVariables)
  return merged as ThemeVariables
}

function fileUrl(file: any): string | null {
  if (typeof file === 'string') {
    return file.trim() || null
  }
  return (file?.original as string) || null
}

// mobile_logo belongs to the brand logo, so a portal logo applies at every breakpoint.
export function getPortalLogo(campaign: any): { desktop: string | null; mobile: string | null } {
  const themeLogo = getThemeVariables(campaign)?.logo
  if (typeof themeLogo === 'string' && themeLogo.trim()) {
    const url = themeLogo.trim()
    return { desktop: url, mobile: url }
  }
  const desktop = fileUrl(campaign?.logo?.file)
  return { desktop, mobile: fileUrl(campaign?.mobile_logo?.file) ?? desktop }
}

export type ButtonPlacement = ActionSlot

// `<action>_button_placement` theme var → where the button renders. Defaults to
// page-bottom.
export function getButtonPlacement(campaign: any, action: 'cancel' | 'return' | 'pause'): ButtonPlacement {
  const v = (getThemeVariables(campaign) as any)?.[`${action}_button_placement`]
  return v === ActionSlot.OrderControls ? ActionSlot.OrderControls : ActionSlot.PageBottom
}

export function styleBool(campaign: any, key: string): boolean | undefined {
  const v = (getThemeVariables(campaign) as any)?.[key]
  if (v == null || v === '') {
    return undefined
  }
  return v === true || v === 'true' || v === 1 || v === '1'
}

function roundness(map: Record<string, string>, value: string | undefined, fallback: string): string {
  return (value && map[value]) || fallback
}

function isTrue(value: string | number | boolean | undefined): boolean {
  return value === 'true' || value === true || value === 1 || value === '1'
}

// 0–100 → 0–1 alpha
function opacity(value: string | number | undefined, fallback = '1'): string {
  if (value === undefined || value === '') {
    return fallback
  }
  const n = Number(value)
  return Number.isNaN(n) ? fallback : String(Math.max(0, Math.min(100, n)) / 100)
}

function px(value: string | number | undefined, fallback: string): string {
  if (value === undefined || value === '') {
    return fallback
  }
  if (typeof value === 'number') {
    return `${value}px`
  }
  return /^\d+$/.test(value.trim()) ? `${value}px` : value
}

// Cap radius so a "fully-rounded" value can't turn a tall card into a stadium.
function clampRadiusPx(value: string, maxPx: number): string {
  const n = parseInt(value, 10)
  return !Number.isNaN(n) && n > maxPx ? `${maxPx}px` : value
}

function cssUrl(value: string | undefined): string | undefined {
  if (!value || !value.trim()) {
    return undefined
  }
  const trimmed = value.trim()
  return /^url\(/i.test(trimmed) ? trimmed : `url("${trimmed}")`
}

// Build a button box-shadow from the *_shadow_* variables; 0 opacity → no shadow.
function buttonShadow(v: any, keyPrefix: string): string {
  const alpha = Number(opacity(v[`${keyPrefix}_shadow_opacity`], '0'))
  if (!alpha) {
    return 'none'
  }
  const h = px(v[`${keyPrefix}_shadow_horizontal_offset`], '0px')
  const vert = px(v[`${keyPrefix}_shadow_vertical_offset`], '0px')
  const blur = px(v[`${keyPrefix}_shadow_blur`], '0px')
  return `${h} ${vert} ${blur} rgba(0, 0, 0, ${alpha})`
}

function buttonStyle(
  t: Record<string, string>,
  prefix: string,
  style: ButtonStyle | undefined,
  fallback: ButtonStyle,
  borderAlpha: number
) {
  const s = style || fallback
  const theme = 'var(--omp-theme-color)'
  const border =
    borderAlpha < 1
      ? `color-mix(in srgb, var(--omp-theme-color) ${Math.round(borderAlpha * 100)}%, transparent)`
      : theme
  if (s === ButtonStyle.Outline) {
    t[`${prefix}-bg`] = 'transparent'
    t[`${prefix}-fg`] = theme
    t[`${prefix}-border`] = border
  } else if (s === ButtonStyle.Text) {
    t[`${prefix}-bg`] = 'transparent'
    t[`${prefix}-fg`] = theme
    t[`${prefix}-border`] = 'transparent'
  } else if (s === ButtonStyle.LowFill) {
    t[`${prefix}-bg`] = 'color-mix(in srgb, var(--omp-theme-color) 14%, var(--omp-card-bg))'
    t[`${prefix}-fg`] = theme
    t[`${prefix}-border`] = 'transparent'
  } else {
    t[`${prefix}-bg`] = theme
    t[`${prefix}-fg`] = 'var(--omp-button-text-color)'
    t[`${prefix}-border`] = border
  }
}

export function buildOmpThemeTokens(v: ThemeVariables | undefined): Record<string, string> {
  const t: Record<string, string> = {}
  if (!v) {
    return t
  }

  if (v.font_family) {
    t['--omp-font-family'] = v.font_family
  }
  if (v.heading_font) {
    t['--omp-heading-font'] = v.heading_font
  }

  if (v.theme_color) {
    t['--omp-theme-color'] = v.theme_color
  }
  if (v.header_background_color) {
    t['--omp-header-color'] = v.header_background_color
  }
  if (v.body_text_color) {
    t['--omp-text-color'] = v.body_text_color
  }
  if (v.button_text_color) {
    t['--omp-button-text-color'] = v.button_text_color
  }
  if (v.button_hover_color) {
    t['--omp-button-hover-color'] = v.button_hover_color
  }
  if (v.links_and_icons_color) {
    t['--omp-link-color'] = v.links_and_icons_color
  }
  if (v.card_background_color) {
    t['--omp-card-bg'] = v.card_background_color
  }
  if (v.card_border_color) {
    const op = opacity(v.card_border_opacity)
    t['--omp-card-border-color'] =
      op === '1' ? v.card_border_color : `color-mix(in srgb, ${v.card_border_color} ${Number(op) * 100}%, transparent)`
  }
  if (v.page_background_color) {
    t['--omp-page-bg'] = v.page_background_color
  }

  const bgImage = cssUrl(v.page_background_image)
  const bgOpacity = opacity(v.page_background_image_opacity)
  if (bgImage) {
    t['--omp-bg-image'] = bgImage
  }
  t['--omp-page-bg-opacity'] = bgOpacity
  t['--omp-page-color-opacity'] = bgImage ? '1' : bgOpacity

  // Header — no separator unless explicitly enabled.
  t['--omp-header-justify'] = LOGO_JUSTIFY[v.desktop_logo_position ?? ''] ?? 'center'
  t['--omp-header-separator'] = isTrue(v.show_header_separator_line) ? '1px' : '0px'

  // Sticky nav blocks. With the blocks on top they live in the header stack, so the
  // whole header sticks; on the side only the nav column does.
  const stickyNav = isTrue(v.enable_sticky_navigational_blocks)
  const navOnTop = v.navigation_blocks_position === 'top'
  t['--omp-header-position'] = stickyNav && navOnTop ? 'sticky' : 'relative'
  t['--omp-nav-col-position'] = stickyNav && !navOnTop ? 'sticky' : 'static'

  // Cards
  t['--omp-card-radius'] = clampRadiusPx(roundness(CARD_ROUNDNESS, v.card_corner_roundness, '12px'), 24)
  t['--omp-card-border-style'] = v.card_border_style ?? BorderStyle.Solid
  t['--omp-card-border-width'] =
    v.card_border_style === BorderStyle.None ? '0px' : clampRadiusPx(px(v.card_border_thickness, '1px'), 4)
  const shadowAlpha = Math.min(0.2, Number(opacity(v.card_shadow_opacity, '0.1')))
  const blur = px(v.card_shadow_blur, '8px')
  // Hairline ring keeps all four edges defined even when the drop is bottom-only.
  t['--omp-card-shadow'] =
    `0 0 0 1px rgba(0, 0, 0, 0.04), ${px(v.card_shadow_horizontal_offset, '0px')} ${px(v.card_shadow_vertical_offset, '2px')} ${blur} rgba(0, 0, 0, ${shadowAlpha})`

  // Buttons
  buttonStyle(
    t,
    '--omp-primary-btn',
    v.primary_button_style,
    ButtonStyle.Fill,
    Number(opacity(v.primary_button_border_opacity))
  )
  buttonStyle(
    t,
    '--omp-secondary-btn',
    v.secondary_button_style,
    ButtonStyle.LowFill,
    Number(opacity(v.secondary_button_border_opacity))
  )
  buttonStyle(
    t,
    '--omp-tertiary-btn',
    v.tertiary_button_style,
    ButtonStyle.Text,
    Number(opacity(v.tertiary_button_border_opacity))
  )
  t['--omp-btn-radius'] = roundness(BUTTON_ROUNDNESS, v.button_corner_roundness, '9999px')
  t['--omp-primary-btn-border-width'] = px(v.primary_button_border_thickness, '1px')
  t['--omp-secondary-btn-border-width'] = px(v.secondary_button_border_thickness, '1px')
  t['--omp-tertiary-btn-border-width'] = px(v.tertiary_button_border_thickness, '1px')
  const shadowFor = (
    style: ButtonStyle | undefined,
    fallback: ButtonStyle,
    key: 'primary_button' | 'secondary_button' | 'tertiary_button'
  ) => ((style || fallback) === ButtonStyle.Text ? 'none' : buttonShadow(v, key))
  t['--omp-primary-btn-shadow'] = shadowFor(v.primary_button_style, ButtonStyle.Fill, 'primary_button')
  t['--omp-secondary-btn-shadow'] = shadowFor(v.secondary_button_style, ButtonStyle.LowFill, 'secondary_button')
  t['--omp-tertiary-btn-shadow'] = shadowFor(v.tertiary_button_style, ButtonStyle.Text, 'tertiary_button')

  // Products
  t['--omp-product-aspect'] = ASPECT_RATIO[v.product_image_aspect_ratio ?? ''] ?? '3 / 4'
  t['--omp-product-radius'] = clampRadiusPx(roundness(CARD_ROUNDNESS, v.product_image_corner_roundness, '12px'), 20)

  // Layout
  t['--omp-max-page-width'] = px(v.max_page_width, '1200px')
  t['--omp-max-content-width'] = px(v.max_content_width, '700px')
  t['--omp-section-padding-y'] = px(v.section_vertical_padding, '32px')
  t['--omp-section-padding-x'] = px(v.section_horizontal_padding, '32px')
  if (v.logo_width) {
    t['--omp-logo-width'] = px(v.logo_width, '200px')
  }

  return t
}

export function applyOmpTheme(v: ThemeVariables | undefined): void {
  if (typeof document === 'undefined') {
    return
  }
  const tokens = buildOmpThemeTokens(v)
  const root = document.documentElement
  Object.entries(tokens).forEach(([key, value]) => root.style.setProperty(key, value))
}
